import React, { FC, useEffect, useMemo } from 'react';

import {
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  useToast,
} from '@chakra-ui/react';
import { FormControl, FormLabel } from '@chakra-ui/form-control';
import { FieldArray, useFormikContext } from 'formik';
import { Select } from 'chakra-react-select';
import { HiMinusCircle } from 'react-icons/hi';
import { useQuery } from 'react-query';

import { fetchSkusByManufacturer } from 'api/sku';
import FormikInput from 'components/primitives/FormikInput';
import { Art } from 'types/art';
import { SKU } from 'types/sku';

import {
  applySkuLineCalculations,
  CreatePurchaseOrderFormValues,
  getEmptySkuLine,
  getSkuArtNumbers,
  getSkuLinesSubtotal,
  NA_ART_NUMBER,
} from './CreatePurchaseOrderModal.utils';

const labelProps = {
  fontSize: '12px',
  color: '#afafaf',
};

type PurchaseOrderSkuLinesProps = {
  manufacturerId: string;
  arts: Art[];
  isLoadingArts?: boolean;
};

const formatCurrency = (value: number) =>
  value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const PurchaseOrderSkuLines: FC<PurchaseOrderSkuLinesProps> = ({
  manufacturerId,
  arts,
  isLoadingArts = false,
}) => {
  const {
    values,
    setFieldValue,
  } = useFormikContext<CreatePurchaseOrderFormValues>();
  const toast = useToast();

  const { data: skus = [], isLoading: isLoadingSkus } = useQuery(
    ['fetch-skus-by-manufacturer', manufacturerId],
    () => fetchSkusByManufacturer(manufacturerId),
    {
      enabled: Boolean(manufacturerId),
      refetchOnMount: 'always',
    },
  );

  const artsByNumber = useMemo(() => {
    const map = new Map<string, Art>();
    arts.forEach((art) => {
      if (art.number) {
        map.set(art.number, art);
      }
    });
    return map;
  }, [arts]);

  const skuOptions = useMemo(
    () =>
      skus.map((sku) => ({
        label: sku.name,
        value: sku._id,
      })),
    [skus],
  );

  const getSkuById = (skuId: string) => skus.find((sku) => sku._id === skuId);

  const getArtOptions = (sku?: SKU) =>
    getSkuArtNumbers(sku).map((artNumber) => {
      const art = artsByNumber.get(artNumber);

      return {
        label: art?.description || artNumber,
        value: artNumber,
      };
    });

  const getArtFields = (artNumber: string) => {
    if (!artNumber) {
      return {
        artNumber: '',
        artId: '',
        artDescription: '',
      };
    }

    const art = artsByNumber.get(artNumber);

    if (!art?._id) {
      if (artNumber === NA_ART_NUMBER) {
        return {
          artNumber,
          artId: '',
          artDescription: 'No art',
        };
      }

      toast({
        status: 'error',
        title: 'Art lookup failed',
        description: `Could not find art for number "${artNumber}"`,
        isClosable: true,
        duration: 4000,
      });

      return {
        artNumber,
        artId: '',
        artDescription: '',
      };
    }

    return {
      artNumber,
      artId: art._id,
      artDescription: art.description ?? '',
    };
  };

  const updateSkuLine = (
    index: number,
    updates: Partial<CreatePurchaseOrderFormValues['skuLines'][number]>,
    source: 'boxQty' | 'totalUnits' | 'sku' = 'boxQty',
  ) => {
    const currentLine = { ...values.skuLines[index], ...updates };
    const selectedSku = getSkuById(currentLine.skuId);
    const nextLine = applySkuLineCalculations(currentLine, selectedSku, source);

    setFieldValue(`skuLines.${index}`, nextLine);
  };

  const handleArtSelect = (index: number, artNumber: string) => {
    const artFields = getArtFields(artNumber);
    setFieldValue(`skuLines.${index}.artNumber`, artFields.artNumber);
    setFieldValue(`skuLines.${index}.artId`, artFields.artId);
    setFieldValue(`skuLines.${index}.artDescription`, artFields.artDescription);
  };

  const handleSkuSelect = (index: number, skuId: string) => {
    const selectedSku = getSkuById(skuId);
    const firstArtNumber = getSkuArtNumbers(selectedSku)[0] ?? '';
    const artFields = getArtFields(firstArtNumber);

    updateSkuLine(
      index,
      {
        skuId,
        ...artFields,
      },
      'sku',
    );
  };

  useEffect(() => {
    values.skuLines.forEach((line, index) => {
      if (!line.skuId) {
        return;
      }

      const selectedSku = getSkuById(line.skuId);

      if (!selectedSku) {
        return;
      }

      const nextLine = applySkuLineCalculations(line, selectedSku, 'boxQty');

      if (
        nextLine.costPerBox !== line.costPerBox ||
        nextLine.costPerUnit !== line.costPerUnit ||
        nextLine.totalCost !== line.totalCost ||
        nextLine.totalUnits !== line.totalUnits
      ) {
        setFieldValue(`skuLines.${index}`, nextLine);
      }
    });
  }, [skus]);

  const subtotal = getSkuLinesSubtotal(values.skuLines);

  return (
    <Box mt={4}>
      <Text fontWeight="semibold" mb={3}>
        Add SKUs
      </Text>

      <FieldArray name="skuLines">
        {({ push, remove }) => (
          <>
            {values.skuLines.map((line, index) => {
              const selectedSku = getSkuById(line.skuId);
              const artOptions = getArtOptions(selectedSku);

              return (
                <Box
                  key={index}
                  mb={4}
                  p={4}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="8px">
                  <Flex justify="end" align="flex-start" mb={2}>
                    <IconButton
                      aria-label="Remove SKU"
                      icon={<HiMinusCircle size="22px" fill="#E54444" />}
                      size="sm"
                      variant="ghost"
                      isDisabled={values.skuLines.length <= 1}
                      onClick={() => remove(index)}
                    />
                  </Flex>

                  <Flex gap={3} flexWrap="wrap">
                    <Box flex={1} minW="220px">
                      <FormControl isRequired>
                        <FormLabel {...labelProps}>Select a SKU</FormLabel>
                        <Select
                          placeholder="Select SKU"
                          isLoading={isLoadingSkus}
                          isDisabled={!manufacturerId}
                          options={skuOptions}
                          value={
                            skuOptions.find(
                              (option) => option.value === line.skuId,
                            ) ?? null
                          }
                          onChange={(option) => {
                            handleSkuSelect(index, option?.value ?? '');
                          }}
                        />
                      </FormControl>
                    </Box>

                    <Box flex={1} minW="220px">
                      <FormControl isRequired>
                        <FormLabel {...labelProps}>Select Art</FormLabel>
                        <Select
                          placeholder="Select art"
                          isDisabled={!line.skuId}
                          isLoading={isLoadingArts}
                          options={artOptions}
                          value={
                            artOptions.find(
                              (option) => option.value === line.artNumber,
                            ) ?? null
                          }
                          onChange={(option) => {
                            handleArtSelect(index, option?.value ?? '');
                          }}
                        />
                      </FormControl>
                    </Box>
                  </Flex>

                  <Flex gap={3} flexWrap="wrap" mt={2}>
                    <FormikInput
                      type="number"
                      name={`skuLines.${index}.boxQty`}
                      label="Box Qty"
                      labelProps={labelProps}
                      containerProps={{ flex: 1, minW: '140px', mt: 0 }}
                      onChange={(value) => {
                        updateSkuLine(
                          index,
                          {
                            boxQty:
                              value === '' || value === undefined
                                ? ''
                                : Number(value),
                          },
                          'boxQty',
                        );
                      }}
                    />
                    <FormikInput
                      type="number"
                      name={`skuLines.${index}.totalUnits`}
                      label="Total Units"
                      labelProps={labelProps}
                      isReadOnly
                      containerProps={{ flex: 1, minW: '140px', mt: 0 }}
                      onChange={(value) => {
                        updateSkuLine(
                          index,
                          {
                            totalUnits:
                              value === '' || value === undefined
                                ? ''
                                : Number(value),
                          },
                          'totalUnits',
                        );
                      }}
                    />
                    <FormikInput
                      type="number"
                      name={`skuLines.${index}.costPerBox`}
                      label="Cost Per Box"
                      labelProps={labelProps}
                      isReadOnly
                      containerProps={{ flex: 1, minW: '140px', mt: 0 }}
                    />
                    <FormikInput
                      type="number"
                      name={`skuLines.${index}.costPerUnit`}
                      label="Cost Per Unit"
                      labelProps={labelProps}
                      isReadOnly
                      containerProps={{ flex: 1, minW: '140px', mt: 0 }}
                    />
                    <FormikInput
                      type="number"
                      name={`skuLines.${index}.totalCost`}
                      label="Total Cost"
                      labelProps={labelProps}
                      isReadOnly
                      containerProps={{ flex: 1, minW: '140px', mt: 0 }}
                    />
                  </Flex>
                </Box>
              );
            })}

            <Button
              w="100%"
              variant="outline"
              onClick={() => push(getEmptySkuLine())}>
              Add SKU
            </Button>
          </>
        )}
      </FieldArray>

      <Flex justify="flex-end" mt={4}>
        <Text fontWeight="bold" fontSize="lg">
          Subtotal: {formatCurrency(subtotal)}
        </Text>
      </Flex>
    </Box>
  );
};

export default PurchaseOrderSkuLines;
